import type { AssistantSuggestion, AssistantSuggestionsResponse } from "@/api/frameworksApi";

/**
 * Pure helpers for panel-open suggestions.
 *
 * Lives in `lib/` alongside the other non-React helpers so the store keeps only state and one action —
 * `store.ts` is already at this repo's 700-line ceiling, and mapping logic here costs it nothing.
 */

/**
 * The rows to render for a response.
 *
 * Empty means empty. There used to be a substitution here — five hardcoded starters stood in whenever
 * the list came back empty — and it was the reason a broken endpoint went unnoticed for so long: a
 * failed generation and a working one were indistinguishable on screen, because both ended as the same
 * five questions on every page.
 *
 * The backend now sends `available: false` and a sentence with every empty list, and the panel renders
 * that sentence. Showing nothing and saying why beats showing something and implying it fits the page.
 */
export function mapSuggestions(
  response: AssistantSuggestionsResponse | null | undefined,
): AssistantSuggestion[] {
  return response?.suggestions ?? [];
}

/**
 * Whether a response's wording came from the backend at all.
 * <p>
 * Kept as the guard on reporting a `source`: a response with nothing in it has no wording to
 * attribute, and crediting those opens to `BASE` would inflate the backend's numbers.
 */
export function isBackendSourced(
  response: AssistantSuggestionsResponse | null | undefined,
): boolean {
  return (response?.suggestions?.length ?? 0) > 0;
}
